<template>
  <div class="experience-feed">
    <h2 class="feed-header">{{ t('title') }}</h2>
    <div v-if="experiences.length" class="feed-list">
      <ExperienceCard
        v-for="(exp, index) in experiences"
        :key="exp.id"
        v-experience-view-tracker="getAnalyticsParams(exp, index)"
        v-bind="exp"
        :position="index"
        data-test-prefix="feed-item"
        @set-favorite="handleSetFavorite(rawExperiences[index])"
        @set-not-favorite="handleSetNotFavorite(rawExperiences[index])"
        @clicked="handleExperienceClick(exp, index)"
      />
    </div>
    <div v-else class="feed-list">
      <ExperienceCardSkeleton v-for="i in skeletonCount" :key="i" :animate="loading" />
    </div>
    <div v-if="hasMoreExperiences" class="feed-actions">
      <UiButton
        class="show-more-button"
        :variant="UiButtonVariant.OUTLINE"
        :color-mode="UiButtonColorMode.NEUTRAL"
        :text="t('showMore')"
        :is-loading="loading"
        @clicked="fetchData"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { UiButton, UiButtonColorMode, UiButtonVariant } from '@experience/core-button-ui';
import { getDisplayCurrencyQuery } from '@experience/core-display-currency-lib';
import { getBootstrapData } from '@experience/core-hostapp-share-lib';
import { ExperienceCard, ExperienceCardSkeleton } from '@experience/home-experience-card';
import { useI18n } from '@experience/i18n';
import { sendFavoriteMindboxEvent } from '@experience/mindbox-events';
import { onMounted, computed, ref } from 'vue';

import ru from '@/features/experienceFeed/i18n/ru';
import type { Experience, ExperienceFavoriteItem } from '@/shared/api/generated/api';
import {
  addExperienceToFavorite,
  getExperienceList,
  getExperiencesFavoritesList,
  removeExperienceFromFavorite,
} from '@/shared/api/generated/api';
import type {
  MainPopularExperienceViewEventParams,
  MainPopularExperienceClickEventParams,
} from '@/shared/api/generated/sogu';
import { useOffsetLimitPagination } from '@/shared/helpers/pagination';
import { useAnalytics } from '@/shared/lib/analytics';
import { mapExperienceToCard, type PreparedExperience } from '@/shared/lib/experience/mapExperienceToCard';
import { useMediaQuery, useScreenSize } from '@/shared/utils/breakpoints';

type MessageSchema = typeof ru;
const { t } = useI18n<{ message: MessageSchema }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const { useViewTracker, trackEvent, trackEventImmediately } = useAnalytics();

const favorites = ref<ExperienceFavoriteItem[]>([]);

const loadFavorites = async () => {
  const { data } = await getExperiencesFavoritesList();
  favorites.value = data ?? [];
};

const rawExperiences = ref<Experience[]>([]);
const hasMoreExperiences = ref(false);

// По 6 экскурсий на страницу в блоке «Популярные экскурсии» (кратно сетке карусели).
const { limit, page, offset } = useOffsetLimitPagination(6);
const loading = ref(true);

const isCustomTablet = useMediaQuery('(min-width: 768px) and (max-width: 1100px)');
const { isMobile } = useScreenSize();
const skeletonCount = computed(() => {
  if (isMobile.value) {
    return 1;
  }

  if (isCustomTablet.value) {
    return 2;
  }

  return 6;
});

const experiences = computed(() => rawExperiences.value.map((exp) => mapExperienceToCard(exp, favorites.value)));

const getAnalyticsParams = (
  exp: PreparedExperience,
  index: number,
): MainPopularExperienceViewEventParams['params'] | MainPopularExperienceClickEventParams['params'] => {
  return {
    experience_id: exp.id,
    position: index,
    full_price: exp.price,
    duration: exp.duration,
    type: exp.type,
    title: exp.title,
    format: exp.format,
    city_name: exp.city,
    guide_id: exp.guideId,
    instant_booking: exp.instantBooking,
    rating: exp.rating,
    review_count: exp.reviewsCount,
    is_paid: exp.paidPromotion,
  };
};

const vExperienceViewTracker = useViewTracker('main:popular:experience.view');
const handleExperienceClick = (exp: PreparedExperience, index: number) =>
  trackEventImmediately('main:popular:experience.click', getAnalyticsParams(exp, index));

const fetchData = async () => {
  loading.value = true;

  let response;

  try {
    [response] = await Promise.all([
      getExperienceList({
        query: {
          limit: limit.value,
          offset: offset.value,
          ...getDisplayCurrencyQuery(),
        },
      }),
      loadFavorites(),
    ]);
  } catch (e) {
    loading.value = false;

    throw e;
  }

  const { data } = response;

  rawExperiences.value = rawExperiences.value.concat(data?.results ?? []);
  hasMoreExperiences.value = Boolean(data?.next);
  page.value += 1;
  loading.value = false;
};

onMounted(() => fetchData());

// Блокирует повторное переключение избранного до завершения текущего запроса.
let favoritesToggling = false;

// Отправляет аналитику намерения и добавляет существующую популярную экскурсию в избранное.
const handleSetFavorite = async (experience: Experience | undefined) => {
  if (!experience || favoritesToggling) {
    return;
  }

  const { id: experienceId } = experience;
  favoritesToggling = true;

  try {
    void trackEvent('main:popular:experience:add-to-favorite.click', { experience_id: experienceId, action: 'add' });

    // Фиксирует намерение пользователя до запроса favorite API.
    sendFavoriteMindboxEvent({
      action: 'add',
      bootstrapData: getBootstrapData(),
      experience,
    });

    const favoriteItem = await addExperienceToFavorite({ path: { experienceId } });

    if (favoriteItem.data) {
      favorites.value = [...favorites.value, favoriteItem.data];
    }
  } finally {
    favoritesToggling = false;
  }
};

// Отправляет аналитику намерения и удаляет существующую популярную экскурсию из избранного.
const handleSetNotFavorite = async (experience: Experience | undefined) => {
  if (!experience || favoritesToggling) {
    return;
  }

  const { id: experienceId } = experience;
  favoritesToggling = true;

  try {
    void trackEvent('main:popular:experience:add-to-favorite.click', { experience_id: experienceId, action: 'remove' });

    // Фиксирует намерение пользователя до запроса favorite API.
    sendFavoriteMindboxEvent({
      action: 'remove',
      bootstrapData: getBootstrapData(),
      experience,
    });

    await removeExperienceFromFavorite({ path: { experienceId } });
    favorites.value = favorites.value.filter((item) => item.item_id !== experienceId);
  } finally {
    favoritesToggling = false;
  }
};
</script>

<style lang="scss" scoped>
.experience-feed {
  width: 100%;
}

.feed-header {
  @include typography.base-800-750-extra;

  @include adaptivity.mobile {
    @include typography.base-700-500-extra;
  }
}

.feed-list {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--alias-space-40) var(--alias-space-24);
  margin-top: var(--alias-space-32);

  @include adaptivity.mobile {
    grid-template-columns: 1fr;
    row-gap: var(--alias-space-24);
    margin-top: var(--alias-space-24);
  }

  @media screen and (width >= 768px) and (width <= 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.feed-actions {
  display: flex;
  justify-content: center;
  padding-top: var(--alias-space-40);

  @include adaptivity.tablet {
    justify-content: stretch;
    padding-top: var(--alias-space-32);
  }

  @include adaptivity.mobile {
    padding-top: var(--alias-space-24);
  }
}

.show-more-button {
  @include adaptivity.tablet {
    width: 100%;
  }
}
</style>
